import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

export interface PdfViewProps {
  fileHref: string;
  title: string;
  active: boolean; // whether pdf-viewer is the selected view type
}

export function PdfView({ fileHref, title, active }: PdfViewProps) {
  const lastObjectUrlRef = useRef<string | null>(null);

  // because we have content-dispostion: attachment headers we need to fetch the pdf manually before embedding or the browser will download it.
  const pdfQuery = useQuery({
    queryKey: ['file-pdf', fileHref],
    queryFn: async () => {
      if (!fileHref) 
        return null;
      const res = await fetch(fileHref);

      if (!res.ok) 
        throw new Error(`Failed to fetch PDF (${res.status})`);

      return await res.blob();
    },
    enabled: active && !!fileHref,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const pdfBlobUrl = useMemo(() => {
    if (!active || !pdfQuery.data)
        return null;
    return URL.createObjectURL(pdfQuery.data);
  }, [pdfQuery.data, active])

  // Final cleanup on unmount
  useEffect(() => {
    return () => {
      if (lastObjectUrlRef.current) URL.revokeObjectURL(lastObjectUrlRef.current);
    };
  }, []);

  if (pdfQuery.isLoading) {
    return <Box display="flex" justifyContent="center" alignItems="center" height="100%"><CircularProgress /></Box>;
  }
  if (pdfQuery.isError) {
    return <Box display="flex" justifyContent="center" alignItems="center" height="100%"><Typography color="error" variant="body2">Failed to load PDF</Typography></Box>;
  }
  if (!pdfBlobUrl) return null;

  return (
    <Box
      component="iframe"
      src={pdfBlobUrl}
      data-testid="pdf-viewer-iframe"
      sx={{ width: '100%', height: '100%', border: 'none' }}
      title={title}
    />
  );
}
